import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { LikeButtonVariants } from '@atoms/LikeButton/LikeButton';
import Text from '@atoms/Text/Text';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import ThemeEnum from '@models/enums/ThemeEnum';
import { IListingColourBar } from '@models/IListing';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import { brightnessByColour } from '@utility/helpers/brightnessByColour';
import classNames from 'classnames';
import { useContext } from 'react';

const ListingColourBar = ({ colourCard, cta, subTitle }: IListingColourBar) => {
  const WishlistButton = useContext(WishlistButtonContext);
  const {
    link,
    colourCode,
    colourHexCode,
    colourName,
    wishlistDrawerLabels,
    mainImage,
  } = colourCard;

  const textColour =
    colourHexCode && colourName
      ? brightnessByColour(`#${colourHexCode}`) > 127
        ? ThemeEnum.Dark
        : ThemeEnum.Light
      : null;

  return (
    <div
      className={classNames(
        textColour === ThemeEnum.Light && !mainImage && 'tw-dark',
        'tw-relative tw-flex tw-flex-col'
      )}
    >
      <div className="gap-x-xs tw-mb-4 tw-flex tw-justify-between">
        <div className="tw-flex-[2]">
          <Text variant="heading-21-light" className="tw-hidden lg:tw-block">
            {subTitle}
          </Text>
          <Text variant="body-18" className="tw-font-light lg:tw-hidden">
            {subTitle}
          </Text>
        </div>
        <Button
          as="link"
          variant="link"
          href={cta.url}
          target={cta.target}
          label={cta.text}
          buttonClasses="!tw-p-0 tw-flex-1 md:tw-hidden max-md:!tw-text-black"
          contentClasses="max-md:!tw-justify-end"
        />
      </div>

      <div
        className={classNames(
          'tw-flex tw-w-full tw-justify-between tw-text-black md:tw-h-[164px] dark:tw-text-white',
          {
            'tw-min-h-24 tw-bg-white md:tw-min-h-[140px] lg:tw-min-h-[225px]':
              mainImage,
            'tw-h-[144px] tw-px-md tw-py-sm': !mainImage,
          }
        )}
        style={!mainImage ? { backgroundColor: `#${colourHexCode}` } : {}}
      >
        {mainImage && (
          <div className="tw-w-[30%] tw-overflow-hidden md:tw-w-32 lg:tw-w-[192px] xl:tw-w-[224px]">
            <div className="tw-h-full tw-w-full">
              <Image
                image={mainImage}
                classes="tw-h-full tw-object-cover"
                imageClasses="tw-h-full tw-w-full tw-object-cover"
              />
            </div>
          </div>
        )}
        <div
          className={classNames('tw-flex tw-flex-col tw-gap-xxs', {
            'tw-my-xs tw-ml-4 tw-mr-auto lg:tw-my-sm lg:tw-ml-8': mainImage,
          })}
        >
          <Text className="tw-block" variant="body-small">
            {colourCode}
          </Text>

          {colourName && (
            <Text variant="ds-headline-4" className="tw-capitalize">
              {colourName.toLowerCase()}
            </Text>
          )}
        </div>

        <div
          className={classNames(
            'tw-relative tw-flex tw-flex-col tw-items-end tw-justify-between md:tw-min-w-fit',
            {
              'tw-py-xs tw-pl-xxs tw-pr-xs lg:tw-py-sm lg:tw-pr-md': mainImage,
            }
          )}
        >
          {WishlistButton && (
            <WishlistButton
              isCard={false}
              card={colourCard}
              cardId={`${link}.html`} // To match existing format
              collection={CardTypeEnum.Colours}
              wishlistDrawerLabels={wishlistDrawerLabels}
              buttonVariant={mainImage ? LikeButtonVariants.Grey : undefined}
            />
          )}
          <Button
            as="link"
            variant="link"
            href={cta.url}
            target={cta.target}
            label={cta.text}
            buttonClasses="!tw-p-0 tw-hidden md:tw-block"
          />
        </div>
      </div>
    </div>
  );
};

export default ListingColourBar;
